(() => {
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
  const PHONE_STRIP = /[\s()+.-]/g;
  const REQUEST_TIMEOUT = 10000;
  const TRACKING_EVENT = 'form_submit';
  const TRACKING_FORM_NAME = 'designer_contact';
  const KLAVIYO_EVENTS_URL = 'https://a.klaviyo.com/client/events/';
  const KLAVIYO_REVISION = '2026-07-15';
  const DESIGNER_METRIC = 'Received Designer Lead';
  const CUSTOMER_METRIC = 'Submitted Designer Contact Form';

  const uniqueId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const decodeRecipient = (encoded) => {
    try {
      const bytes = Uint8Array.from(atob(encoded || ''), (char) => char.charCodeAt(0));
      return new TextDecoder().decode(bytes).trim();
    } catch (error) {
      return '';
    }
  };

  const trackKlaviyoEvent = async (account, metric, email, properties, keepalive = false) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    try {
      const response = await fetch(`${KLAVIYO_EVENTS_URL}?company_id=${encodeURIComponent(account)}`, {
        method: 'POST',
        keepalive,
        headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties,
              unique_id: properties.submission_id,
              metric: { data: { type: 'metric', attributes: { name: metric } } },
              profile: { data: { type: 'profile', attributes: { email } } },
            },
          },
        }),
        signal: controller.signal,
      });
      if (!response.ok) {
        console.warn(`designer-contact-form: Klaviyo rejected "${metric}" with HTTP ${response.status}.`);
      }
      return response.ok;
    } finally {
      clearTimeout(timer);
    }
  };

  class DesignerContactForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = this.querySelector('[data-designer-contact-form]');
      if (!this.form) return;
      this.initialized = true;
      this.attempted = false;
      this.statusRegion = this.querySelector('[data-status]');
      this.submitButton = this.form.querySelector('[data-submit]');
      this.honeypot = this.form.querySelector('[data-honeypot]');
      this.maxLength = parseInt(this.dataset.maxLength, 10) || 0;
      this.onSubmit = this.onSubmit.bind(this);
      this.onRevalidate = this.onRevalidate.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onRevalidate);
      this.form.addEventListener('blur', this.onRevalidate, true);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.initialized = false;
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.removeEventListener('input', this.onRevalidate);
      this.form.removeEventListener('blur', this.onRevalidate, true);
    }

    get fields() {
      return this.form ? Array.from(this.form.querySelectorAll('[data-validate]')) : [];
    }

    message(key) {
      return this.dataset[key] || '';
    }

    validateField(input) {
      const kind = input.dataset.validate;
      const value = (input.value || '').trim();
      if (input.dataset.required === 'true' && value === '') return this.message('errorRequired');
      if (value === '') return '';
      if (kind === 'email' && !EMAIL_PATTERN.test(value)) return this.message('errorEmail');
      if (kind === 'phone' && !/^\d{7,15}$/.test(value.replace(PHONE_STRIP, ''))) {
        return this.message('errorPhone');
      }
      if (kind === 'comment' && this.maxLength > 0 && value.length > this.maxLength) {
        return this.message('errorMaxLength');
      }
      return '';
    }

    setFieldError(input, message) {
      const field = input.closest('[data-field]');
      const errorElement = field ? field.querySelector('[data-error]') : null;
      if (message) {
        input.setAttribute('aria-invalid', 'true');
        if (field) field.classList.add('is-invalid');
        if (errorElement) {
          errorElement.textContent = message;
          errorElement.hidden = false;
          input.setAttribute('aria-describedby', errorElement.id);
        }
        return;
      }
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
      if (field) field.classList.remove('is-invalid');
      if (errorElement) {
        errorElement.textContent = '';
        errorElement.hidden = true;
      }
    }

    setStatus(message, state) {
      if (!this.statusRegion) return;
      this.statusRegion.classList.remove('is-error', 'is-notice');
      this.statusRegion.textContent = message || '';
      if (message && state) this.statusRegion.classList.add(state);
    }

    onRevalidate(event) {
      if (!this.attempted) return;
      const input = event.target;
      if (!input || !input.dataset || !input.dataset.validate) return;
      this.setFieldError(input, this.validateField(input));
    }

    onSubmit(event) {
      event.preventDefault();
      this.attempted = true;

      if (this.honeypot && this.honeypot.value.trim() !== '') {
        this.renderSuccess();
        return;
      }

      let firstInvalid = null;
      this.fields.forEach((input) => {
        const message = this.validateField(input);
        this.setFieldError(input, message);
        if (message && !firstInvalid) firstInvalid = input;
      });

      if (firstInvalid) {
        this.setStatus('');
        firstInvalid.focus();
        return;
      }

      const account = (this.dataset.klaviyoKey || '').trim();
      const recipient = decodeRecipient(this.dataset.recipient);
      if (account === '' || recipient === '') {
        this.setStatus(this.message('notConfigured'), 'is-notice');
        console.warn('designer-contact-form: no Klaviyo key or designer email, nothing was sent.');
        return;
      }

      this.send(account, recipient);
    }

    lead(recipient) {
      const data = new FormData(this.form);
      const value = (key) => (data.get(key) || '').toString().trim();
      const fields = {
        designer_email: recipient,
        designer_name: value('designer_name'),
        designer_location: value('designer_location'),
        designer_handle: value('designer_handle'),
        customer_name: value('name'),
        customer_email: value('email'),
        customer_phone: value('phone'),
        comment: value('comment'),
        page_handle: value('page_handle'),
        page_url: value('page_url'),
      };
      // An unchanged retry keeps its id so Klaviyo dedupes it; edited fields are a new submission.
      const fingerprint = JSON.stringify(fields);
      if (fingerprint !== this.leadFingerprint) {
        this.leadFingerprint = fingerprint;
        this.submissionId = uniqueId();
      }
      return { submission_id: this.submissionId, ...fields };
    }

    async send(account, recipient) {
      this.classList.add('is-loading');
      if (this.submitButton) this.submitButton.disabled = true;
      this.setStatus('');

      const lead = this.lead(recipient);
      // The lead lands on the designer's profile, so the Klaviyo flow emails the designer, not the visitor.
      const succeeded = await trackKlaviyoEvent(account, DESIGNER_METRIC, recipient, lead).catch(() => false);
      this.classList.remove('is-loading');

      if (succeeded) {
        // Only after the designer got the lead, so a visitor auto-reply never confirms a lost submission.
        // keepalive survives the tab closing; browsers that refuse it with a CORS preflight get a plain retry, deduped by unique_id.
        trackKlaviyoEvent(account, CUSTOMER_METRIC, lead.customer_email, lead, true)
          .catch(() => trackKlaviyoEvent(account, CUSTOMER_METRIC, lead.customer_email, lead))
          .catch(() => false);
        // Same queue as snippets/klaviyo-identify.liquid: klaviyo.js drains it before or after it loads.
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['identify', { email: lead.customer_email }]);
        this.trackSubmission(lead);
        this.renderSuccess();
        return;
      }
      if (this.submitButton) this.submitButton.disabled = false;
      this.setStatus(this.message('errorMessage'), 'is-error');
    }

    // Real submissions only: the honeypot path also renders success. No contact details go to dataLayer.
    trackSubmission(lead) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: TRACKING_EVENT,
        form_name: TRACKING_FORM_NAME,
        designer_handle: lead.designer_handle,
        page_handle: lead.page_handle,
      });
    }

    renderSuccess() {
      const template = this.querySelector('template[data-success]');
      const holder = document.createElement('div');
      holder.className = 'designer-contact-form__success';
      holder.setAttribute('tabindex', '-1');
      if (template) holder.appendChild(template.content.cloneNode(true));
      this.setStatus('');
      this.form.replaceWith(holder);
      holder.focus();
    }
  }

  if (!customElements.get('designer-contact-form')) {
    customElements.define('designer-contact-form', DesignerContactForm);
  }
})();
